import axiosInstance from 'service/axiosInstance';

export type MediaType = 'IMAGE' | 'VIDEO' | 'LINK' | 'DOCUMENT_PDF' | 'DOCUMENT_WORD' | 'DOCUMENT_OTHER';

export interface GetUploadUrlResponse {
  status: boolean;
  message: string;
  ipAddress: string;
  projectUuid: string;
  workItemUuid: string;
  mediaUuid: string;
  mediaType: MediaType; 
  url: string; // The presigned S3 URL
  description: string;
  originalFileName: string;
}

export interface GetUploadUrlPayload {
  projectUuid: string;
  workItemUuid?: string; // Optional for new tasks? Need to verify
  originalFileName: string;
  mediaType: MediaType;
  description?: string;
}

export const getUploadUrl = async (payload: GetUploadUrlPayload): Promise<GetUploadUrlResponse> => {
  const response = await axiosInstance.post('/pmo/media/get-upload-url', payload);
  const data: GetUploadUrlResponse = response.data?.response ?? response.data;

  // Backend returns HTTP 200 even on errors — check status in the body
  if (!data.status) {
    throw new Error(data.message || 'Failed to get upload URL');
  }

  return data;
};

export const uploadFileToS3 = async (url: string, file: File) => {
  // Use native fetch without auth interceptors for the S3 presigned PUT
  const response = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });
  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
  }
  return true;
};

export const markUploadComplete = async (projectUuid: string, mediaUuid: string) => {
  const response = await axiosInstance.post(`/pmo/${projectUuid}/media/${mediaUuid}/mark-uploaded`);
  const data = response.data?.response ?? response.data;

  // Backend returns HTTP 200 even on errors — check status in the body
  if (data && data.status === false) {
    throw new Error(data.message || 'Failed to mark upload as complete');
  }

  return data;
};

export const deleteMedia = async (projectUuid: string, mediaUuid: string) => {
  const response = await axiosInstance.delete(`/pmo/${projectUuid}/media/${mediaUuid}/delete`);
  const data = response.data?.response ?? response.data;

  // Backend returns HTTP 200 even on errors — check status in the body
  if (data && data.status === false) {
    throw new Error(data.message || 'Failed to delete attachment');
  }

  return data;
};

export const getDownloadUrl = async (projectUuid: string, mediaUuid: string) => {
  const response = await axiosInstance.get(`/pmo/${projectUuid}/media/${mediaUuid}/get-download-url`);
  const data = response.data?.response ?? response.data;

  // Backend returns HTTP 200 even on errors — check status in the body
  if (data && data.status === false) {
    throw new Error(data.message || 'Failed to get download URL');
  }

  return data;
};

